<template>
  <div>
    <multiselect
      ref="multiSelectInput"
      v-model="selectedList"
      :options="options"
      :multiple="true"
      :taggable="taggable"
      :hide-selected="true"
      :close-on-select="!taggable"
      :clear-on-select="!taggable"
      track-by="name"
      label="name"
      :placeholder="placeholder"
      @tag="tagAdded"
      :max="0"
      @input="valueUpdated"
      @open="dropdownOpened"
      :class="{'is-invalid': hasError()}"
      v-if="!loading">
      <template slot="tag" slot-scope="props">
        <span class="multiselect__tag" :style="{'background-color': tagBGColour(props.option.colour)}">
          <span v-text="props.option.name" :style="{'color': tagFGColour(tagBGColour(props.option.colour))}"></span>
          <i aria-hidden="true" tabindex="1" @keypress.enter.prevent="props.remove(props.option)"  @mousedown.prevent="props.remove(props.option)" class="multiselect__tag-icon"></i>
        </span>
      </template>
      <template slot="maxElements" v-if="taggable">&nbsp;</template>
      <template slot="maxElements" v-if="!taggable">Select from options below</template>
      <template slot="noOptions" v-if="taggable">&nbsp;</template>
    </multiselect>
    <div class="form-invalid-feedback" v-if="hasError()">
      {{getError()}}
    </div>
    <Spinner v-if="loading" />
    <span v-if="note" class="form-field-note">{{note}}</span>
  </div>
</template>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style>

.multiselect__tags {
  border: 1px solid #e5e6e7;
  min-height: 35px;
  padding-top: 4px;
  padding-bottom: 4px;
  border-radius: 1px;
  font-size: 12px;
}
.is-invalid .multiselect__tags {
  border: 1px solid #dc3545;
}
.multiselect__placeholder {
  margin-bottom: 2px;
  font-size: 0.9rem;
}
.multiselect li:not(.multiselect__element) .multiselect__option {
  display: none;
}
.multiselect__option--highlight {
  background-color: #1982C3;
}
.multiselect__option--highlight:after {
  background-color: #1982C3;
}
.multiselect__tag {
  background-color: #1982C3;
  border-radius: 4px;
  margin-bottom: 0px;
  margin-top: 4px;
}
.multiselect__tag-icon {
  background-color: none;
  transition: all 0s ease 0s;
}
.multiselect__tag-icon:focus,.multiselect__tag-icon:hover {
  background-color: #000000;
  opacity: 0.2;
}
.multiselect__tag-icon::after {
  color: #ffffff;
  opacity: 0.5;
}
.multiselect, .multiselect__input {
  font-size: 0.9rem;
}
</style>

<script>
import Multiselect from 'vue-multiselect'
import Colour from '@/mixins/colour'
import formField from '@/mixins/formField'

export default {
  name: 'FormMultiSelect',
  mixins: [Colour, formField],
  props: {
    options: {
      type: Array,
      default: () => ([])
    },
    loading: {
      type: Boolean,
      default: false
    },
    value: {
      type: Array,
      default: () => ([])
    },
    taggable: {
      type: Boolean,
      default: false
    },
    placeholder: String,
    note: String,
    field: String
  },
  watch: {
    value: function(newVal, oldVal) {
      this.updateSelectedList()
    },
    options: function(newVal, oldVal) {
      this.updateSelectedList()
    }
  },
  components: { 
    Multiselect
  },
  data () {
    return {
      selectedList: [],
    }
  },
  mounted() {
    this.updateSelectedList()
  },
  methods: {
    valueUpdated(value) {
      this.$emit('input', value.map(item => { return item.value }))
    },
    updateSelectedList() {
      if(this.$props.options == null || this.$props.value == null)
        return

      if(this.$props.taggable) {
        this.$data.selectedList = this.value.map(tag => {
          return {
            name: tag,
            value: tag
          }
        })
        return
      }
      this.$data.selectedList = this.$props.options.filter(option => {
        return this.$props.value.indexOf(option.value) != -1
      })
    },
    tagAdded(newTag) {
      const tag = {
        name: newTag,
        value: newTag
      }
      this.$data.selectedList.push(tag)

      this.$emit('input', this.$data.selectedList.map(item => item.value))
    },
    dropdownOpened() {
      // Manually adjust the open direction to adjust for parent scrollable containers
      var mutliSelectEl = this.$refs.multiSelectInput.$el
      var maxHeight = this.$refs.multiSelectInput.maxHeight

      var spaceAbove = mutliSelectEl.getBoundingClientRect().top
      var spaceBelow = window.innerHeight - mutliSelectEl.getBoundingClientRect().bottom

      var scrollParent = this.findScrollParent(mutliSelectEl)
      if(scrollParent) {
        spaceBelow = scrollParent.getBoundingClientRect().bottom - mutliSelectEl.getBoundingClientRect().bottom
        spaceAbove = mutliSelectEl.getBoundingClientRect().top - scrollParent.getBoundingClientRect().top
      }

      const hasEnoughSpaceBelow = spaceBelow > maxHeight

      if (hasEnoughSpaceBelow || spaceBelow > spaceAbove) {
        this.$refs.multiSelectInput.preferredOpenDirection = 'below'
        this.$refs.multiSelectInput.optimizedHeight = Math.min(spaceBelow, maxHeight)
      } else {
        this.$refs.multiSelectInput.preferredOpenDirection = 'above'
        this.$refs.multiSelectInput.optimizedHeight = Math.min(spaceAbove, maxHeight)
      }
    },
    tagFGColour(bgColour) {
      return this.textColourForBackground(bgColour)
    },
    tagBGColour(colour) {
      return colour ? colour : '#1982C3'
    },
    hasError() {
      return this.getFieldError(this.$props.field).length > 0
    },
    getError() {
      return this.getFieldError(this.$props.field)
    }
  }
}
</script>

